import React, { createContext, createRef, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { fullscreenVideoElementSupport, getAvailableSubtitles, getSelectSubtitleIndex, } from '@polestar/component-warehouse-react/components/video-player/VideoPlayer.helpers';
import { motionValue, useMotionValue } from 'framer-motion';
import { closeFullscreen, isInFullscreen, openFullscreen, } from '@polestar/component-warehouse-react/helpers/fullscreen';
const noProviderFunction = (handleName) => () => {
    console.warn(`Trying to use header context consumer, but no context provider for "${handleName}". Please make sure you use the useHeader inside a <PolestarApp /> or check if there are multiple @polestar/component-warehouse-react versions installed due to (for example) shared modules hard dependencies.`);
};
/**
 * @internal
 */
export const initialContext = {
    playerRef: createRef(),
    menuOpen: false,
    setMenuOpen: noProviderFunction('setMenuOpen'),
    fullscreen: false,
    setFullscreen: noProviderFunction('setFullscreen'),
    muted: false,
    setMuted: noProviderFunction('setMuted'),
    volume: 1,
    setVolume: noProviderFunction('setVolume'),
    playing: false,
    setPlaying: noProviderFunction('setPlaying'),
    togglePlaying: noProviderFunction('togglePlaying'),
    canPlay: true,
    buffering: false,
    duration: 1,
    availableSubtitles: [],
    selectedSubtitle: 0,
    setSelectedSubtitle: noProviderFunction('setSelectedSubtitle'),
    ended: false,
    startedPlaying: false,
    suspended: false,
    hasBeenSuspended: false,
    nativeVideoPlayerVisible: false,
    currentTime: motionValue(0),
};
const VideoPlayerContext = createContext({
    videoPlayerRef: createRef(),
    ...initialContext,
});
export const useVideoPlayer = () => useContext(VideoPlayerContext);
export const useMediaProvider = ({ mediaRef, type, ...props }) => {
    var _a, _b;
    const playerRef = useRef(null);
    const [startedPlaying, setStartedPlaying] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [muted, setMuted] = useState(
    /*
     * muted property is not rendered by React in the DOM (https://github.com/facebook/react/issues/10389)
     * but important in some cases (Chrome requires it, but Firefox/Safari work without it)
     */
    // @ts-ignore - prefixed feature to let it work in Chrome
    ((_a = mediaRef.current) === null || _a === void 0 ? void 0 : _a.webkitAudioDecodedByteCount) === 0 || !!props.muted);
    // @ts-ignore - prefixed feature to let it work in Chrome
    const audioDecodedBytes = (_b = mediaRef.current) === null || _b === void 0 ? void 0 : _b.webkitAudioDecodedByteCount;
    useEffect(() => {
        var _a;
        if (audioDecodedBytes === 0 && ((_a = mediaRef.current) === null || _a === void 0 ? void 0 : _a.autoplay)) {
            /**
             * Chrome is more precise with autoPlay and muted but React does not parse muted property well and initially `mediaRef.current?.webkitAudioDecodedByteCount=undefined` and is not updated anymore. But if it is updated to become 0 than it should be muted when using autoPlay
             */
            setMuted(true);
        }
    }, [audioDecodedBytes, mediaRef]);
    const [volume, setVolume] = useState(!!props.muted ? 0 : 1);
    const [suspended, setSuspended] = useState(false);
    const [hasBeenSuspended, setHasBeenSuspended] = useState(false);
    const mediaCurrentTime = useMotionValue(0);
    const [playing, setPlaying] = useState(props.autoPlay || false);
    const [canPlay, setCanPlay] = useState(false);
    const [buffering, setBuffering] = useState(true);
    const [duration, setDuration] = useState(0);
    const [availableSubtitles, setAvailableSubtitles] = useState([]);
    const [selectedSubtitle, setSelectedSubtitle] = useState(-1);
    const [ended, setEnded] = useState(false);
    /**
     * Progress
     */
    useEffect(() => {
        if (mediaRef.current) {
            const media = mediaRef.current;
            const handleStartedPlaying = () => {
                if ((media === null || media === void 0 ? void 0 : media.currentTime) && (media === null || media === void 0 ? void 0 : media.currentTime) !== 0) {
                    mediaCurrentTime.set(media === null || media === void 0 ? void 0 : media.currentTime);
                }
            };
            const handleDurationChange = () => setDuration(media.duration);
            media.addEventListener('timeupdate', handleStartedPlaying);
            media.addEventListener('durationchange', handleDurationChange);
            return () => {
                media.removeEventListener('timeupdate', handleStartedPlaying);
                media.removeEventListener('durationchange', handleDurationChange);
            };
        }
    }, [mediaRef, mediaCurrentTime]);
    // timeupdate is not updated often enough, so we update currentTime on every frame while playing
    useEffect(() => {
        if (playing) {
            let raf;
            const updateCurrentTime = () => {
                if (mediaRef.current) {
                    const playerCurrentTime = mediaRef.current.currentTime;
                    mediaCurrentTime.set(playerCurrentTime);
                }
                raf = requestAnimationFrame(updateCurrentTime);
            };
            updateCurrentTime();
            return () => {
                cancelAnimationFrame(raf);
            };
        }
    }, [playing, mediaCurrentTime, mediaRef]);
    /**
     * Reset on new source
     */
    const isAutoPlay = !!props.autoPlay;
    const currentSources = useRef('');
    const reset = useCallback((startedPlaying = false) => {
        var _a;
        setPlaying(isAutoPlay || false);
        setCanPlay(false);
        setStartedPlaying(startedPlaying);
        setBuffering(true);
        setMenuOpen(false);
        setEnded(false);
        if (mediaCurrentTime.get() === duration) {
            mediaCurrentTime.set(0);
        }
        (_a = mediaRef === null || mediaRef === void 0 ? void 0 : mediaRef.current) === null || _a === void 0 ? void 0 : _a.load();
    }, [mediaRef, mediaCurrentTime, setPlaying, duration, isAutoPlay]);
    useEffect(() => {
        if ((mediaRef === null || mediaRef === void 0 ? void 0 : mediaRef.current) && typeof MutationObserver !== 'undefined') {
            const videoPlayer = mediaRef.current;
            const checkSources = () => {
                const newSources = [videoPlayer, ...videoPlayer === null || videoPlayer === void 0 ? void 0 : videoPlayer.querySelectorAll('source')]
                    .map((source) => source.getAttribute('src') || '')
                    .join(';');
                const differentSources = newSources !== currentSources.current;
                if (differentSources && currentSources.current) {
                    reset();
                }
                if (currentSources.current === null || differentSources) {
                    currentSources.current = newSources;
                }
            };
            const observer = new MutationObserver(checkSources);
            checkSources();
            observer.observe(videoPlayer, { attributes: true, subtree: true, childList: true });
            return () => observer.disconnect();
        }
    }, [mediaRef, reset]);
    /**
     * Play changes
     */
    const togglePlaying = useCallback(() => {
        var _a, _b, _c;
        if (mediaRef.current) {
            if ((_a = mediaRef.current) === null || _a === void 0 ? void 0 : _a.paused) {
                // ensure that replay icon is changed to play icon as also the seekbar is reset
                if (type === MediaType.VIDEO) {
                    ended && reset(true);
                }
                (_b = mediaRef === null || mediaRef === void 0 ? void 0 : mediaRef.current) === null || _b === void 0 ? void 0 : _b.play().catch((error) => {
                    console.error('VideoPlayerContext: play failed due to', error);
                });
            }
            else {
                (_c = mediaRef === null || mediaRef === void 0 ? void 0 : mediaRef.current) === null || _c === void 0 ? void 0 : _c.pause();
            }
        }
    }, [mediaRef, ended, reset, type]);
    useEffect(() => {
        if (mediaRef.current) {
            const media = mediaRef.current;
            const handlePlay = () => {
                setPlaying(!(media === null || media === void 0 ? void 0 : media.paused));
                setStartedPlaying(true);
                setSuspended(false);
                setEnded(false);
            };
            const handleCanPlay = () => {
                if (startedPlaying) {
                    // ensure that only at the start it waits for onCanPlayThrough event
                    // while during playpack the media will try to play (even for some frames)
                    // and then this is needed:
                    setCanPlay(true);
                    setBuffering(false);
                }
                // Loaded via SSR doesn't trigger the onDurationChange
                setDuration(media.duration);
            };
            const handleSuspend = () => {
                if ((media === null || media === void 0 ? void 0 : media.paused) && !startedPlaying) {
                    setSuspended(true);
                    setHasBeenSuspended(true);
                    setPlaying(false);
                }
            };
            const handleCanPlayThrough = () => {
                setCanPlay(true);
                setBuffering(false);
                // Loaded via SSR doesn't trigger the onDurationChange
                setDuration(media.duration);
            };
            const handleWaiting = () => {
                var _a, _b;
                setCanPlay(mediaRef.current
                    ? ((_a = mediaRef.current) === null || _a === void 0 ? void 0 : _a.readyState) >= ((_b = mediaRef.current) === null || _b === void 0 ? void 0 : _b.HAVE_CURRENT_DATA)
                    : false);
                setBuffering(true);
            };
            const handleLoadedMetaData = () => {
                setCanPlay(true);
                // Loaded via SSR doesn't trigger the onDurationChange
                setDuration(media.duration);
            };
            const handleEnded = () => {
                setEnded(true);
                setPlaying(false);
            };
            // on SSR the media can already be loaded and will never fire the `loadedmetadata` event
            // If we have the duration, assume metadata is loaded
            if (media.readyState === media.HAVE_METADATA) {
                handleLoadedMetaData();
            }
            else if (media.readyState === media.HAVE_ENOUGH_DATA ||
                media.readyState === media.HAVE_FUTURE_DATA ||
                media.readyState === media.HAVE_CURRENT_DATA) {
                handleCanPlayThrough();
            }
            media.addEventListener('play', handlePlay);
            media.addEventListener('pause', handlePlay);
            media.addEventListener('suspend', handleSuspend);
            media.addEventListener('canplay', handleCanPlay);
            media.addEventListener('canplaythrough', handleCanPlayThrough);
            media.addEventListener('waiting', handleWaiting);
            media.addEventListener('loadedmetadata', handleLoadedMetaData);
            media.addEventListener('ended', handleEnded);
            return () => {
                media.removeEventListener('play', handlePlay);
                media.removeEventListener('pause', handlePlay);
                media.removeEventListener('suspend', handleSuspend);
                media.removeEventListener('canplaythrough', handleCanPlay);
                media.removeEventListener('waiting', handleWaiting);
                media.removeEventListener('canplay', handleCanPlayThrough);
                media.removeEventListener('loadedmetadata', handleLoadedMetaData);
                media.removeEventListener('ended', handleEnded);
            };
        }
    }, [mediaRef, setPlaying, startedPlaying]);
    /**
     * Volume
     **/
    useEffect(() => {
        if (mediaRef === null || mediaRef === void 0 ? void 0 : mediaRef.current) {
            if (!!muted) {
                setVolume(0);
            }
            else {
                setVolume(volume);
            }
            if (mediaRef.current.muted !== muted) {
                // sync
                setMuted(!!muted);
                // this is needed for Safari on iOS while it does not provide ability to change the
                // volume on iOS https://developer.apple.com/library/archive/documentation/AudioVideo/Conceptual/Using_HTML5_Audio_Video/Device-SpecificConsiderations/Device-SpecificConsiderations.html
                if (!muted && volume === 0) {
                    setVolume(1);
                }
            }
        }
    }, [mediaRef, muted, volume]);
    useEffect(() => {
        if (mediaRef.current) {
            if (!!muted) {
                mediaRef.current.setAttribute('muted', 'true');
            }
            else {
                mediaRef.current.removeAttribute('muted');
            }
            mediaRef.current.muted = muted;
        }
    }, [mediaRef, muted]);
    useEffect(() => {
        if (mediaRef.current) {
            mediaRef.current.volume = volume;
        }
    }, [mediaRef, volume]);
    /**
     * Subtitles
     */
    useEffect(() => {
        const media = mediaRef.current;
        if (media) {
            const textTracksList = media.textTracks;
            const updateSubtitleTracks = () => {
                setAvailableSubtitles(getAvailableSubtitles(textTracksList));
                setSelectedSubtitle(getSelectSubtitleIndex(textTracksList));
            };
            media.addEventListener('loadstart', updateSubtitleTracks);
            updateSubtitleTracks();
            if (textTracksList === null || textTracksList === void 0 ? void 0 : textTracksList.addEventListener) {
                textTracksList.addEventListener('addtrack', updateSubtitleTracks);
                textTracksList.addEventListener('removetrack', updateSubtitleTracks);
                textTracksList.addEventListener('change', updateSubtitleTracks);
            }
            return () => {
                media.removeEventListener('loadstart', updateSubtitleTracks);
                if (textTracksList === null || textTracksList === void 0 ? void 0 : textTracksList.removeEventListener) {
                    textTracksList.removeEventListener('addtrack', updateSubtitleTracks);
                    textTracksList.removeEventListener('removetrack', updateSubtitleTracks);
                    textTracksList.removeEventListener('change', updateSubtitleTracks);
                }
            };
        }
    }, [mediaRef]);
    return {
        playerRef,
        menuOpen,
        setMenuOpen,
        muted,
        setMuted,
        volume,
        setVolume,
        playing,
        setPlaying,
        togglePlaying,
        canPlay,
        buffering,
        duration,
        availableSubtitles,
        selectedSubtitle,
        setSelectedSubtitle,
        ended,
        startedPlaying,
        suspended,
        hasBeenSuspended,
        currentTime: mediaCurrentTime,
    };
};
/**
 * @internal
 */
export var MediaType;
(function (MediaType) {
    MediaType["VIDEO"] = "video";
    MediaType["AUDIO"] = "audio";
})(MediaType || (MediaType = {}));
export const VideoPlayerProvider = ({ controls, children, ...props }) => {
    const internalRef = useRef(null);
    const videoPlayerRef = controls.videoPlayerRef || internalRef;
    const { fullscreen, setFullscreen } = controls;
    const mediaProps = useMediaProvider({
        type: MediaType.VIDEO,
        mediaRef: videoPlayerRef,
        ...props,
    });
    const [nativeVideoPlayerVisible, setNativeVideoPlayerVisible] = useState(false);
    /**
     * Fullscreen
     */
    useEffect(() => {
        var _a, _b;
        const fullscreenHandler = (e) => {
            if (!fullscreenVideoElementSupport) {
                // this is needed while on mobile the videoplayer goes into fullscreen
                // and the custom styled subtitles need to be hidden and the native
                // ones to be shown again
                if (e.type === 'webkitbeginfullscreen') {
                    setNativeVideoPlayerVisible(true);
                }
                else if (e.type === 'webkitendfullscreen') {
                    setNativeVideoPlayerVisible(false);
                }
            }
            setFullscreen === null || setFullscreen === void 0 ? void 0 : setFullscreen(isInFullscreen());
        };
        document.addEventListener('fullscreenchange', fullscreenHandler);
        document.addEventListener('webkitfullscreenchange', fullscreenHandler);
        (_a = videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('webkitendfullscreen', fullscreenHandler);
        (_b = videoPlayerRef.current) === null || _b === void 0 ? void 0 : _b.addEventListener('webkitbeginfullscreen', fullscreenHandler);
        const videoPlayer = videoPlayerRef.current;
        return () => {
            document.removeEventListener('fullscreenchange', fullscreenHandler);
            document.removeEventListener('webkitfullscreenchange', fullscreenHandler);
            if (videoPlayer) {
                videoPlayer.removeEventListener('webkitendfullscreen', fullscreenHandler);
                videoPlayer.removeEventListener('webkitbeginfullscreen', fullscreenHandler);
            }
        };
    }, [videoPlayerRef, setFullscreen]);
    // We need to use useMemo so the fullscreen toggle is in the same click action
    // Otherwise Safari would disregard it, because we aren't allowed to set fullscreen outside a user interaction
    useMemo(() => {
        if (fullscreen !== undefined) {
            if (!fullscreen) {
                closeFullscreen();
            }
            else {
                // Fullscreens the whole player on devices that support it to keep the custom video controls visible
                if (fullscreenVideoElementSupport) {
                    openFullscreen(mediaProps.playerRef);
                }
                else {
                    // fullscreens the video only (on mobile)
                    openFullscreen(videoPlayerRef);
                }
            }
        }
    }, [fullscreen, mediaProps.playerRef, videoPlayerRef]);
    return (React.createElement(VideoPlayerContext.Provider, { value: {
            videoPlayerRef,
            ...mediaProps,
            fullscreen: fullscreen || false,
            setFullscreen,
            nativeVideoPlayerVisible,
        } }, children));
};
